import DateOfBirthField from '../components/DateOfBirthField';
import { isAdult } from '../utils/agePolicy';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaUser, FaEnvelope, FaLock, FaPhone, FaSpinner, FaMapMarkerAlt, FaGlobe, FaShieldAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { fetchWithAuth } from '../services/api';
import { trackVisit } from '../services/visitTrackingService';
import EmailVerificationDialog from '../components/EmailVerificationDialog';
import { countries } from '../utils/countries';
import { getLanguageFromPath, hasValidAuthentication } from '../services/auth';
import { 
  isDevelopmentEnvironment, 
  isTestSiteKey, 
  handleCaptchaToken, 
  getCaptchaErrorMessage,
  generateDummyToken
} from '../utils/captcha-utils';

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    dateOfBirth: '',
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    address: '',
    city: '',
    country: 'MK',
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [isCaptchaLoaded, setIsCaptchaLoaded] = useState(false);
  const [captchaWidgetId, setCaptchaWidgetId] = useState(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [captchaSiteKey, setCaptchaSiteKey] = useState('1x00000000000000000000AA'); // fallback test key
  const captchaContainerRef = useRef(null);

  // Redirect if already logged in
  useEffect(() => {
    if (hasValidAuthentication()) {
      const lang = getLanguageFromPath();
      navigate(`/${lang}`, { replace: true });
    }
  }, [navigate]);

  const handleCaptchaSuccess = (token) => {
    const processedToken = handleCaptchaToken(token, captchaSiteKey);
    
    setCaptchaToken(processedToken);
    setErrors(prev => ({ ...prev, captcha: '' }));
  };

  const handleCaptchaError = (errorCode) => {
    if (isDevelopmentEnvironment() && isTestSiteKey(captchaSiteKey)) {
      const dummyToken = generateDummyToken();
      setCaptchaToken(dummyToken);
      setErrors(prev => ({ ...prev, captcha: '' }));
      return;
    }
    
    setCaptchaToken('');
    const errorMessage = getCaptchaErrorMessage(errorCode, captchaSiteKey);
    
    setErrors(prev => ({
      ...prev,
      captcha: errorMessage
    }));
  };

  const handleCaptchaExpire = () => {
    setCaptchaToken('');
    setErrors(prev => ({
      ...prev,
      captcha: 'CAPTCHA expired. Please solve it again.'
    }));
  };

  const initializeCaptcha = () => {
    if (!window.turnstile) {
      console.warn('Turnstile not loaded yet');
      return;
    }

    if (!captchaContainerRef.current) {
      console.warn('Captcha container ref not available');
      return;
    }

    if (captchaWidgetId !== null) {
      console.log('Widget already initialized, skipping');
      return;
    }

    // Validate site key before rendering
    if (!captchaSiteKey) {
      console.warn('No site key available, waiting...');
      return;
    }

    const container = captchaContainerRef.current;
    
    // Clean up any existing widgets completely
    if (container.querySelector('[data-cf-turnstile]') || container.querySelector('iframe')) {
      console.log('Cleaning up existing CAPTCHA widget');
      container.innerHTML = '';
    }
    
    // Also try to remove any widgets that Turnstile might be tracking
    try {
      if (window.turnstile && window.turnstile.remove) {
        window.turnstile.remove();
      }
    } catch (e) {
      console.log('Could not call turnstile.remove():', e.message);
    }
    
    console.log('Rendering CAPTCHA with site key:', captchaSiteKey);
    
    try {
      const widgetId = window.turnstile.render(container, {
        sitekey: captchaSiteKey,
        callback: handleCaptchaSuccess,
        'error-callback': handleCaptchaError,
        'expired-callback': handleCaptchaExpire,
        theme: 'light',
        size: 'normal',
        retry: 'auto',
        'refresh-expired': 'auto'
      });
      
      console.log('CAPTCHA widget rendered with ID:', widgetId);
      setCaptchaWidgetId(widgetId);
    } catch (error) {
      console.error('Error rendering CAPTCHA:', error);
      
      if (error.message && error.message.includes('already been rendered')) {
        console.log('Widget already rendered, forcing cleanup...');
        container.innerHTML = '';
        
        setTimeout(() => {
          try {
            const retryWidgetId = window.turnstile.render(container, {
              sitekey: captchaSiteKey,
              callback: handleCaptchaSuccess,
              'error-callback': handleCaptchaError,
              'expired-callback': handleCaptchaExpire,
              theme: 'light',
              size: 'normal',
              retry: 'auto',
              'refresh-expired': 'auto'
            });
            console.log('CAPTCHA widget re-rendered with ID:', retryWidgetId);
            setCaptchaWidgetId(retryWidgetId);
          } catch (retryError) {
            console.error('Failed to re-render CAPTCHA:', retryError);
            setErrors(prev => ({
              ...prev,
              captcha: 'Failed to initialize CAPTCHA. Please refresh the page.'
            }));
          }
        }, 100);
      } else {
        setErrors(prev => ({
          ...prev,
          captcha: 'Failed to initialize CAPTCHA. Please refresh the page.'
        }));
      }
    }
  };

  const resetCaptcha = () => {
    console.log('Resetting CAPTCHA widget');
    
    if (captchaWidgetId !== null && window.turnstile) {
      try {
        window.turnstile.reset(captchaWidgetId);
        setCaptchaToken('');
        console.log('CAPTCHA widget reset successfully');
        return;
      } catch (error) {
        console.warn('Could not reset CAPTCHA widget, will reinitialize:', error);
      }
    }
    
    // If reset fails or widget doesn't exist, force full reinitialization
    console.log('Force reinitializing CAPTCHA');
    if (captchaContainerRef.current) {
      captchaContainerRef.current.innerHTML = '';
    }
    
    setCaptchaWidgetId(null);
    setCaptchaToken('');
    setCaptchaKey(prev => prev + 1);
  };


  useEffect(() => {
    // Track registration page visit
    trackVisit('REGISTER');

    const fetchCaptchaConfig = async () => {
      try {
        const config = await fetchWithAuth('/auth/captcha-config', { isPublic: true });
        console.log('CAPTCHA Config:', config);
        console.log('Site Key:', config.siteKey);
        console.log('Environment:', config.environment);
        setCaptchaSiteKey(config.siteKey);
      } catch (error) {
        console.error('Failed to fetch CAPTCHA config:', error);
      }
    };
    
    fetchCaptchaConfig();
  }, []);

  useEffect(() => {
    let isEffectActive = true;
    
    if (window.turnstile) {
      if (isEffectActive) {
        setIsCaptchaLoaded(true);
      }
      return;
    }

    const existingScript = document.querySelector('script[src*="turnstile"]');
    if (existingScript) {
      const handleExistingLoad = () => {
        if (isEffectActive) {
          setIsCaptchaLoaded(true);
        }
      };
      
      existingScript.addEventListener('load', handleExistingLoad);
      
      return () => {
        isEffectActive = false;
        existingScript.removeEventListener('load', handleExistingLoad);
      };
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    script.id = 'turnstile-script';
    
    const handleLoad = () => {
      if (isEffectActive) {
        setIsCaptchaLoaded(true);
      }
    };
    
    const handleError = () => {
      if (isEffectActive) {
        setErrors(prev => ({
          ...prev,
          captcha: 'Failed to load CAPTCHA. Please refresh the page.'
        }));
      }
    };
    
    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);

    document.head.appendChild(script);

    return () => {
      isEffectActive = false;
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
      
      const ourScript = document.getElementById('turnstile-script');
      if (ourScript) {
        ourScript.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (isCaptchaLoaded && window.turnstile && captchaContainerRef.current && captchaSiteKey) {
      // Clean up existing widget before initializing
      if (captchaWidgetId !== null) {
        console.log('Cleaning up previous widget before re-initialization');
        try {
          window.turnstile.remove(captchaWidgetId);
        } catch (e) {
          console.log('Could not remove widget:', e.message);
        }
        setCaptchaWidgetId(null);
      }
      
      const timer = setTimeout(() => {
        if (captchaWidgetId === null) {
          initializeCaptcha();
        }
      }, 300);
      
      return () => {
        clearTimeout(timer);
        // Cleanup on unmount or re-render
        if (captchaWidgetId !== null && window.turnstile) {
          console.log('Cleaning up CAPTCHA widget on unmount');
          try {
            window.turnstile.remove(captchaWidgetId);
          } catch (e) {
            console.log('Could not remove widget on unmount:', e.message);
          }
        }
      };
    }
  }, [isCaptchaLoaded, captchaKey, captchaSiteKey]);

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[0-9]{8,15}$/;

    if (!formData.fullName.trim()) {
      newErrors.fullName = t('register.validation.required');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('register.validation.required');
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = t('register.validation.invalidEmail');
    }

    if (!formData.password) {
      newErrors.password = t('register.validation.required');
    } else if (formData.password.length < 8) {
      newErrors.password = t('register.validation.passwordLength');
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('register.validation.required');
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('register.validation.passwordMismatch');
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t('register.validation.required');
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = t('register.validation.invalidPhone');
    }

    if (!formData.address.trim()) {
      newErrors.address = t('register.validation.required');
    }

    if (!formData.city.trim()) {
      newErrors.city = t('register.validation.required');
    }

    if (!formData.country.trim()) {
      newErrors.country = t('register.validation.required');
    }

    if (!isAdult(formData.dateOfBirth)) newErrors.age = t('agePolicy.invalid');

    if (!acceptTerms) {
      newErrors.acceptTerms = t('register.validation.acceptTerms');
    }

    if (!captchaToken) {
      newErrors.captcha = 'Please complete the CAPTCHA verification';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    if (errors.submit) {
      setErrors(prev => ({ ...prev, submit: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setRegistrationSuccess(false);
    setErrors(prev => ({ ...prev, submit: '' }));

    if (validateForm()) {
      setIsLoading(true);
      const { confirmPassword, ...backendData } = formData;

      const requestData = {
        ...backendData,
        ageConfirmed: acceptTerms,
        termsAccepted: acceptTerms,
        captchaToken: captchaToken
      };

      try {
        const data = await fetchWithAuth('/auth/register', {
          method: 'POST',
          body: requestData,
        });

        if (data.success) {
          setRegistrationSuccess(true);
          setFormData({
            dateOfBirth: '',
            fullName: '',
            email: '',
            password: '',
            confirmPassword: '',
            phone: '',
            address: '',
            city: '',
            country: 'MK',
          });
          setAcceptTerms(false);
          setCaptchaToken('');
          setErrors({});
          setRegisteredEmail(formData.email);
          setShowVerificationDialog(true);
          
          resetCaptcha();
        } else {
          let errorMessage = data.message || t('register.error.submit') || 'Registration failed.';
          
          if (data.message && data.message.toLowerCase().includes('captcha')) {
            errorMessage = 'CAPTCHA verification failed. Please try again.';
            resetCaptcha();
          } else if (data.message && (
            data.message.toLowerCase().includes('email address already in use') ||
            data.message.toLowerCase().includes('email already exists') ||
            data.message.toLowerCase().includes('already in use')
          )) {
            errorMessage = t('register.error.emailExists');
          }
          
          setErrors(prev => ({
            ...prev,
            submit: errorMessage
          }));
        }
      } catch (err) {
        let errorMessage = t('register.error.network') || 'Network error during registration.';
        
        if (err.message) {
          if (err.message.toLowerCase().includes('captcha')) {
            errorMessage = 'CAPTCHA verification failed. Please try again.';
            resetCaptcha();
          } else if (err.message.toLowerCase().includes('email address already in use') ||
              err.message.toLowerCase().includes('email already exists') ||
              err.message.toLowerCase().includes('already in use')) {
            errorMessage = t('register.error.emailExists');
          } else if (!err.message.includes('Failed to fetch') && 
                     !err.message.includes('NetworkError') &&
                     !err.message.includes('network')) {
            errorMessage = err.message;
          }
        }
        
        setErrors(prev => ({
          ...prev,
          submit: errorMessage 
        }));
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleTermsChange = (e) => {
    setAcceptTerms(e.target.checked);
    if (errors.acceptTerms) {
      setErrors(prev => ({
        ...prev,
        acceptTerms: ''
      }));
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex flex-col justify-start py-6 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="sm:mx-auto sm:w-full sm:max-w-4xl">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {t('register.title')}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('register.haveAccount')}{' '}
            <Link 
              to={`/${window.location.pathname.split('/')[1] || 'mk'}/login`} 
              className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
            >
              {t('register.loginHere')}
            </Link>
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-4xl">
        <div className="bg-white py-8 px-6 shadow-xl sm:rounded-xl sm:px-10 border border-gray-200">
          {registrationSuccess ? (
            <div 
              className="rounded-md bg-green-100 p-4 text-center"
              role="alert"
              data-testid="register-success-message"
            >
              <p className="text-sm font-medium text-green-800">
                {t('register.successMessage') || 'Registration successful!'}
              </p>
              <p className="mt-2 text-sm text-green-700">
                <Link 
                  to={`/${window.location.pathname.split('/')[1] || 'mk'}/login`} 
                  className="font-medium text-green-800 hover:text-green-600 underline"
                >
                  {t('register.proceedToLogin') || 'Proceed to Login'}
                </Link>
              </p>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Two-column grid for desktop, single column for mobile */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-6">
                  {/* Full Name */}
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
                      {t('register.fullName')}
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center justify-center pointer-events-none">
                        <FaUser className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        id="fullName"
                        name="fullName"
                        type="text"
                        required
                        value={formData.fullName}
                        onChange={handleChange}
                        aria-invalid={!!errors.fullName}
                        aria-describedby={errors.fullName ? "fullName-error" : undefined}
                        className={`appearance-none block w-full pl-10 pr-3 py-3 border ${errors.fullName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
                        data-testid="register-fullname-input"
                      />
                    </div>
                    {errors.fullName && (
                      <p className="mt-1 text-sm text-red-600" id="fullName-error">{errors.fullName}</p>
                    )}
                  </div>

                  <DateOfBirthField prefix="register" value={formData.dateOfBirth} onChange={handleChange} error={errors.age} />

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      {t('register.email')}
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center justify-center pointer-events-none">
                        <FaEnvelope className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? "email-error" : undefined}
                        className={`appearance-none block w-full pl-10 pr-3 py-3 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
                        data-testid="register-email-input"
                      />
                    </div>
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600" id="email-error">{errors.email}</p>
                    )}
                  </div>

                  {/* Password */}
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      {t('register.password')}
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center justify-center pointer-events-none">
                        <FaLock className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        id="password"
                        name="password"
                        type="password"
                        required
                        value={formData.password}
                        onChange={handleChange}
                        aria-invalid={!!errors.password}
                        aria-describedby={errors.password ? "password-error" : undefined}
                        className={`appearance-none block w-full pl-10 pr-3 py-3 border ${errors.password ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
                        data-testid="register-password-input"
                      />
                    </div>
                    {errors.password && (
                      <p className="mt-1 text-sm text-red-600" id="password-error">{errors.password}</p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                      {t('register.confirmPassword')}
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center justify-center pointer-events-none">
                        <FaLock className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        required
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        aria-invalid={!!errors.confirmPassword}
                        aria-describedby={errors.confirmPassword ? "confirmPassword-error" : undefined}
                        className={`appearance-none block w-full pl-10 pr-3 py-3 border ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
                        data-testid="register-confirm-password-input"
                      />
                    </div>
                    {errors.confirmPassword && (
                      <p className="mt-1 text-sm text-red-600" id="confirmPassword-error">{errors.confirmPassword}</p>
                    )}
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                      {t('register.phone')}
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center justify-center pointer-events-none">
                        <FaPhone className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        aria-invalid={!!errors.phone}
                        aria-describedby={errors.phone ? "phone-error" : undefined}
                        className={`appearance-none block w-full pl-10 pr-3 py-3 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
                        data-testid="register-phone-input"
                      />
                    </div>
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-600" id="phone-error">{errors.phone}</p>
                    )}
                  </div>

                  {/* Address */}
                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                      {t('register.address')}
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center justify-center pointer-events-none">
                        <FaMapMarkerAlt className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        id="address"
                        name="address"
                        type="text"
                        required
                        value={formData.address}
                        onChange={handleChange}
                        aria-invalid={!!errors.address}
                        aria-describedby={errors.address ? "address-error" : undefined}
                        className={`appearance-none block w-full pl-10 pr-3 py-3 border ${errors.address ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
                        data-testid="register-address-input"
                      />
                    </div>
                    {errors.address && (
                      <p className="mt-1 text-sm text-red-600" id="address-error">{errors.address}</p>
                    )}
                  </div>

                  {/* City */}
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                      {t('register.city')}
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center justify-center pointer-events-none">
                        <FaGlobe className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        id="city"
                        name="city"
                        type="text"
                        required
                        value={formData.city}
                        onChange={handleChange}
                        aria-invalid={!!errors.city}
                        aria-describedby={errors.city ? "city-error" : undefined}
                        className={`appearance-none block w-full pl-10 pr-3 py-3 border ${errors.city ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
                        data-testid="register-city-input"
                      />
                    </div>
                    {errors.city && (
                      <p className="mt-1 text-sm text-red-600" id="city-error">{errors.city}</p>
                    )}
                  </div>

                  {/* Country */}
                  <div>
                    <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                      {t('register.country')}
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center justify-center pointer-events-none">
                        <FaGlobe className="h-4 w-4 text-gray-400" />
                      </div>
                      <select
                        id="country"
                        name="country"
                        required
                        value={formData.country}
                        onChange={handleChange}
                        aria-invalid={!!errors.country}
                        aria-describedby={errors.country ? "country-error" : undefined}
                        className={`appearance-none block w-full pl-10 pr-3 py-3 border ${errors.country ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
                        data-testid="register-country-input"
                      >
                        <option value="">{t('register.selectCountry')}</option>
                        {countries.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    {errors.country && (
                      <p className="mt-1 text-sm text-red-600" id="country-error">{errors.country}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* CAPTCHA Section - Full Width */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex flex-col items-center space-y-4">
                  <div className="flex items-center space-x-2">
                    <FaShieldAlt className="h-5 w-5 text-gray-500" />
                    <label className="text-sm font-medium text-gray-700">
                      Security Verification
                    </label>
                  </div>
                  
                  <div className="flex flex-col items-center space-y-3 w-full">
                    <div 
                      key={captchaKey}
                      ref={captchaContainerRef}
                      className="min-h-[78px] flex items-center justify-center w-full max-w-[320px] mx-auto overflow-hidden"
                      data-testid="register-captcha-widget"
                    />
                  </div>
                  
                  {errors.captcha && (
                    <p className="text-sm text-red-600 text-center max-w-md">{errors.captcha}</p>
                  )}
                </div>
              </div>

              {/* Terms and Conditions - Full Width */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center flex-shrink-0">
                    <input
                      required
                      id="acceptTerms"
                      name="acceptTerms"
                      type="checkbox"
                      checked={acceptTerms}
                      onChange={handleTermsChange}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                      data-testid="register-terms-checkbox"
                    />
                  </div>
                  <div className="text-sm leading-5">
                    <label htmlFor="acceptTerms" className="font-medium text-gray-700 cursor-pointer">
                      {t('register.acceptTerms')}{' '}
                      <Link 
                        to={`/${window.location.pathname.split('/')[1] || 'mk'}/terms-and-conditions`}
                        className="text-blue-600 hover:text-blue-500 underline transition-colors break-words"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('register.termsAndConditions')}
                      </Link>
                    </label>
                  </div>
                </div>
                {errors.acceptTerms && (
                  <p className="mt-2 text-sm text-red-600 ml-7">{errors.acceptTerms}</p>
                )}
              </div>

              {/* Error Message */}
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <div className="text-red-600 text-sm font-medium" data-testid="register-error-message">
                    {errors.submit}
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isLoading || !captchaToken}
                  className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] disabled:hover:scale-100"
                  data-testid="register-submit-button"
                >
                  {isLoading ? (
                    <>
                      <FaSpinner className="animate-spin h-4 w-4 mr-2" />
                      {t('register.submitting') || 'Registering...'}
                    </>
                  ) : (
                    t('register.submit')
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      
      {/* Email Verification Dialog */}
      {showVerificationDialog && (
        <EmailVerificationDialog
          userEmail={registeredEmail}
          onClose={() => {
            setShowVerificationDialog(false);
            setRegistrationSuccess(false);
          }}
        />
      )}
    </div>
  );
};

export default Register;
